"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { ReactNode } from "react";
import BlurredPlaceholder from "@/app/(components)/generics/BlurredPlaceholder/BlurredPlaceholder";
import Button, { ButtonType } from "@/app/(components)/generics/Button/Button";
import EditableText from "@/app/(components)/generics/EditableText/EditableText";
import IconEnterprise from "@/app/(components)/generics/Icons/IconEnterprise";
import IconGoal from "@/app/(components)/generics/Icons/IconGoal";
import IconMoney from "@/app/(components)/generics/Icons/IconMoney";
import IconUser from "@/app/(components)/generics/Icons/IconUser";
import IconWorld from "@/app/(components)/generics/Icons/IconWorld";
import PointsLoader from "@/app/(components)/generics/loaders/PointsLoader/PointsLoader";
import { useAuthUser } from "@/app/(components)/specifics/wrappers/AuthProvider/AuthProvider";
import { useCanEditContent } from "@/app/(components)/specifics/wrappers/BrandProvider/BrandProvider";
import { updateAvatarField } from "@/app/actions/client-avatar/update/action";
import { useFetchAvatarAbout } from "@/app/hooks/fetchs/client-avatar/useFetchAvatarTabs";
import { ModalStore } from "@/app/stores/ModalStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { AvatarField, UserClientAvatar } from "@/app/types/client-avatar/UserClientAvatar";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import { Limits } from "@/app/vars/Limits";
import { AvatarQuerySubKey, QueryKey } from "@/app/vars/QueryKeys";
import styles from "../AvatarDetail.module.css";
import { getTranslatedSource } from "./AvatarDetailAbout.logic";
import { AvatarDetailCard } from "./AvatarDetailCard/AvatarDetailCard";
import cardStyles from "./AvatarDetailCard/AvatarDetailCard.module.css";
import AvatarScoresGrid from "./AvatarScoresGrid/AvatarScoresGrid";
import ScoreCardHeader from "./ScoreCardHeader/ScoreCardHeader";

type Props = DefaultProps & {
  avatar: UserClientAvatar;
  isLoading?: boolean;
  isAnalyzing?: boolean;
};

export default function AvatarDetailAbout(props: Props) {
  const T = useT();
  const canEdit = useCanEditContent();
  const { avatar, isLoading, params, isAnalyzing = false } = props;
  const user = useAuthUser();
  const isPlaceholder = !user;
  const router = useRouter();
  const queryClient = useQueryClient();

  const { data: aboutData, isLoading: isFetching } = useFetchAvatarAbout({
    avatarId: avatar.id,
    enabled: !isPlaceholder,
  });

  const fullAvatar = { ...avatar, ...(aboutData || {}) } as UserClientAvatar;
  const isLoadingTab = isLoading || isFetching;

  const handleSave = async (fieldKey: AvatarField, val: string | string[]) => {
    try {
      let finalValue: string | string[] = val;
      if (fieldKey === AvatarField.AGE_RANGE) {
        const separator = (val as string).includes("-") ? "-" : ",";
        finalValue = (val as string)
          .split(separator)
          .map((v) => v.trim())
          .filter(Boolean);
      } else if (fieldKey === AvatarField.LOCATION_RANGE) {
        finalValue = (val as string)
          .split(",")
          .map((v) => v.trim())
          .filter(Boolean);
      }
      await updateAvatarField(avatar.offerId, fieldKey, finalValue);
      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.CLIENT_AVATAR,
          AvatarQuerySubKey.DETAIL,
          avatar.id,
          AvatarQuerySubKey.ABOUT,
        ],
      });
      queryClient.invalidateQueries({ queryKey: [QueryKey.MESSAGE_SEQUENCE] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.PROSPECT_SEARCH_SUMMARY] });
      router.refresh();
    } catch (error) {
      console.error(T(TKey.AVATAR_UPDATE_ERROR), error);
    }
  };

  const renderAttributeCard = (
    key: AvatarField,
    label: string,
    placeholder: string,
    icon: ReactNode,
  ) => {
    const value = fullAvatar[key as keyof UserClientAvatar];
    const displayValue = Array.isArray(value)
      ? key === AvatarField.AGE_RANGE
        ? value.join("-")
        : value.join(", ")
      : value;

    return (
      <div className={styles.attributeCard}>
        <p className={styles.attributeLabel}>
          <span className={styles.attributeIcon}>{icon}</span>
          <span>{label}</span>
        </p>
        {isPlaceholder ? (
          <BlurredPlaceholder rows={[{ cells: [{ width: "60px" }] }]} />
        ) : (
          <EditableText
            value={String(displayValue || "")}
            onSave={(val) => handleSave(key, val)}
            className={styles.attributeValue}
            fitContent={true}
            placeholder={placeholder}
            maxLength={Limits.MAX_AVATAR_SHORT_TEXT}
            disabled={isAnalyzing || !canEdit}
          />
        )}
      </div>
    );
  };

  if (isLoadingTab) {
    return (
      <div className={styles.loaderContainer}>
        <PointsLoader
          centered
          delay={Limits.CLIENT_AVATAR_LOADER_DELAY}
          color="var(--color-green-3)"
        />
      </div>
    );
  }

  return (
    <div className={styles.tabContent}>
      <AvatarDetailCard
        title={T(TKey.CLIENT_AVATAR_DETAIL_ABOUT_SUMMARY_TITLE)}
        isPlaceholder={isPlaceholder}
        placeholderConfig={[{ cells: [{ width: "100%" }] }, { cells: [{ width: "70%" }] }]}
      >
        {isPlaceholder ? null : (
          <EditableText
            value={fullAvatar.snapshotSummary || ""}
            onSave={(val) => handleSave(AvatarField.SNAPSHOT_SUMMARY, val)}
            placeholder={T(TKey.CLIENT_AVATAR_DETAIL_ABOUT_SUMMARY_PLACEHOLDER)}
            className={cardStyles.textBody}
            multiline={true}
            maxLength={Limits.MAX_AVATAR_SENTENCE}
            disabled={isAnalyzing || !canEdit}
          />
        )}
      </AvatarDetailCard>

      <div className={styles.attributesGrid}>
        {renderAttributeCard(
          AvatarField.AGE_RANGE,
          T(TKey.CLIENT_AVATAR_DETAIL_ABOUT_AGE),
          T(TKey.CLIENT_AVATAR_DETAIL_ABOUT_AGE_PLACEHOLDER),
          <IconUser />,
        )}
        {renderAttributeCard(
          AvatarField.LOCATION_RANGE,
          T(TKey.CLIENT_AVATAR_DETAIL_ABOUT_LOCATION),
          T(TKey.CLIENT_AVATAR_DETAIL_ABOUT_LOCATION_PLACEHOLDER),
          <IconWorld />,
        )}
        {renderAttributeCard(
          AvatarField.GENDER,
          T(TKey.CLIENT_AVATAR_DETAIL_ABOUT_GENDER),
          T(TKey.CLIENT_AVATAR_DETAIL_ABOUT_GENDER_PLACEHOLDER),
          <IconUser />,
        )}
        {renderAttributeCard(
          AvatarField.ROLE,
          T(TKey.CLIENT_AVATAR_DETAIL_ABOUT_ROLE),
          T(TKey.CLIENT_AVATAR_DETAIL_ABOUT_ROLE_PLACEHOLDER),
          <IconEnterprise />,
        )}
        {renderAttributeCard(
          AvatarField.MARKET,
          T(TKey.CLIENT_AVATAR_DETAIL_ABOUT_MARKET),
          T(TKey.CLIENT_AVATAR_DETAIL_ABOUT_MARKET_PLACEHOLDER),
          <IconGoal />,
        )}
        {renderAttributeCard(
          AvatarField.INCOME_RANGE,
          T(TKey.CLIENT_AVATAR_DETAIL_ABOUT_INCOME),
          T(TKey.CLIENT_AVATAR_DETAIL_ABOUT_INCOME_PLACEHOLDER),
          <IconMoney />,
        )}
      </div>

      <div className={styles.scoreCardWrapper}>
        <ScoreCardHeader
          params={params}
          showInfo={
            (fullAvatar.scoreFit == null || fullAvatar.scoreFit === 0) &&
            (fullAvatar.scoreConfidence == null || fullAvatar.scoreConfidence === 0) &&
            (fullAvatar.scorePain == null || fullAvatar.scorePain === 0)
          }
        />
        <AvatarScoresGrid avatar={fullAvatar} isPlaceholder={isPlaceholder} params={params} />
      </div>

      <div className={styles.textSectionsGrid}>
        <AvatarDetailCard
          title={T(TKey.CLIENT_AVATAR_DETAIL_ABOUT_PRIORITY_TITLE)}
          isPlaceholder={isPlaceholder}
          placeholderConfig={[
            { cells: [{ width: "100%" }] },
            { cells: [{ width: "80%" }] },
            { cells: [{ width: "60%" }] },
          ]}
        >
          {isPlaceholder ? (
            <p className={cardStyles.textBody}>
              {T(TKey.CLIENT_AVATAR_DETAIL_ABOUT_PRIORITY_PLACEHOLDER_TEXT)}
            </p>
          ) : (
            <EditableText
              value={fullAvatar.priority || ""}
              onSave={(val) => handleSave(AvatarField.PRIORITY, val)}
              placeholder={T(TKey.CLIENT_AVATAR_DETAIL_ABOUT_CARD_INPUT_PLACEHOLDER)}
              className={cardStyles.textBody}
              multiline={true}
              maxLength={Limits.MAX_AVATAR_PARAGRAPH}
              disabled={isAnalyzing || !canEdit}
            />
          )}
        </AvatarDetailCard>

        <div className={styles.sourcesBox}>
          <div className={styles.sourcesHeader}>
            <p className={styles.sourcesTitle}>
              {T(TKey.CLIENT_AVATAR_DETAIL_ABOUT_SOURCES_TITLE)}
            </p>
          </div>
          <div className={styles.tagsList}>
            {isPlaceholder ? (
              <>
                <span className={styles.tag}>{T(TKey.AVATAR_DATA_SOURCE_CALLS)}</span>
                <span className={styles.tag}>{T(TKey.AVATAR_DATA_SOURCE_COMMUNITIES)}</span>
                <span className={styles.tag}>{T(TKey.AVATAR_DATA_SOURCE_SURVEYS)}</span>
              </>
            ) : !fullAvatar.dataSources || fullAvatar.dataSources.length === 0 ? (
              <span className={styles.tag}>{getTranslatedSource("MANUAL", T)}</span>
            ) : (
              fullAvatar.dataSources.map((source, index) => (
                <span key={index} className={styles.tag}>
                  {getTranslatedSource(source, T)}
                </span>
              ))
            )}
          </div>
        </div>
      </div>

      {isPlaceholder && (
        <div className={styles.ctaContainer}>
          <Button
            min={false}
            type={ButtonType.PRIMARY}
            text={T(TKey.CLIENT_AVATAR_CREATE_BUTTON)}
            onClick={() => ModalStore.getState().openSignIn(params)}
          />
        </div>
      )}
    </div>
  );
}
